// src/pages/customer/Scan.jsx
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import { loyalty as loyaltyApi } from '../../api';
import { scanQRFromFile } from '../../utils/qrShare';
import { ReceiptCard } from './CustomerReceipt';

export default function CustomerScan() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const html5QrRef = useRef(null);
  const fileInputRef = useRef(null);

  const pickFromGallery = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ''; // сбрасываем, чтобы можно было выбрать тот же файл повторно
    if (!file) return;
    setLoading(true);
    setResult(null);
    // На всякий случай останавливаем камеру, если она запущена
    try { await stopScan(); } catch (err) {}
    try {
      const decoded = await scanQRFromFile(file);
      await processQR(decoded);
    } catch (err) {
      setResult({ success: false, error: t('customerScan.errorNoQRInImage') });
      setLoading(false);
    }
  };

  const startScan = async () => {
    setResult(null);
    setScanning(true);
    try {
      html5QrRef.current = new Html5Qrcode('qr-reader');
      await html5QrRef.current.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        async (decodedText) => {
          await stopScan();
          await processQR(decodedText);
        },
        () => {}
      );
    } catch (e) {
      setScanning(false);
      setResult({ success: false, error: t('customerScan.errorCamera') });
    }
  };

  const stopScan = async () => {
    if (html5QrRef.current) {
      try { await html5QrRef.current.stop(); html5QrRef.current.clear(); } catch (e) {}
    }
    setScanning(false);
  };

  const processQR = async (text) => {
    setLoading(true);
    try {
      let shopId, token;
      // Пытаемся как JSON (нормальный формат: {"shopId":"...","token":"..."})
      try {
        const data = JSON.parse(text);
        shopId = data.shopId;
        token  = data.token;
      } catch (parseErr) {
        // Не JSON — старый формат, просто строка-токен
        // (Бэк должен найти продажу только по токену.)
        token  = text.trim();
        shopId = null;
      }
      if (!token) throw new Error(t('customerScan.errorInvalid'));
      const res = await loyaltyApi.redeem(shopId, token);
      setResult({ success: true, points: res.pointsEarned, total: res.total, receipt: res.receipt || null });
    } catch (e) {
      setResult({ success: false, error: e.message || 'Не удалось начислить баллы' });
    }
    setLoading(false);
  };

  useEffect(() => { return () => { stopScan(); }; }, []);

  return (
    <div className="page" style={{ paddingBottom: 24 }}>
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate('/')}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <div>
          <p style={{ color: 'var(--text3)', fontSize: 11, fontWeight: 700 }}>{t('customerHome.scanReceipt').toUpperCase()}</p>
          <h1 className="page-title">{t('customerScan.title')}</h1>
        </div>
      </div>

      <div style={{ padding: '0 16px' }}>
        {result && (
          <div className="card fade-up" style={{ marginBottom: 20, background: result.success ? 'rgba(46,204,113,0.1)' : 'rgba(232,65,42,0.1)', borderColor: result.success ? 'var(--green)' : 'var(--accent)', textAlign: 'center', padding: '24px' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>{result.success ? '🎉' : '❌'}</div>
            {result.success ? (
              result.points > 0 ? (
                <>
                  <p style={{ fontSize: 20, fontWeight: 800, color: 'var(--green)', marginBottom: 4 }}>+{result.points} {t('common.stars')}!</p>
                  <p style={{ color: 'var(--text2)', fontSize: 14 }}>{t('customerScan.successTitle')}</p>
                </>
              ) : (
                <p style={{ fontSize: 18, fontWeight: 800, color: 'var(--green)' }}>{t('receipt.saved')}</p>
              )
            ) : (
              <>
                <p style={{ fontSize: 16, fontWeight: 700, color: 'var(--accent)', marginBottom: 4 }}>{t('common.error')}</p>
                <p style={{ color: 'var(--text2)', fontSize: 14 }}>{result.error}</p>
              </>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'center' }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setResult(null)}>
                {t('customerScan.scanAgain')}
              </button>
              <button className="btn btn-primary btn-sm" onClick={() => navigate('/')}>
                {t('customerPay.toHome')}
              </button>
            </div>
          </div>
        )}

        {!scanning && !result && !loading && (
          <div className="card" style={{ marginBottom: 20, textAlign: 'center', padding: '32px 20px' }}>
            <div style={{ fontSize: 64, marginBottom: 16 }}>📱</div>
            <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>{t("customerScan.startTitle")}</h3>
            <p style={{ color: 'var(--text2)', fontSize: 14, lineHeight: 1.6, marginBottom: 20 }}>
              {t("customerScan.startHint")}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button className="btn btn-primary" onClick={startScan}>📷 {t("customerScan.openCamera")}</button>
              <button className="btn btn-secondary" onClick={pickFromGallery}>🖼️ {t("customerScan.fromGallery")}</button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleFileSelected}
            />
          </div>
        )}

        {loading && (
          <div className="card" style={{ textAlign: 'center', padding: '32px' }}>
            <div className="spinner" style={{ margin: '0 auto 16px' }} />
            <p style={{ color: "var(--text2)" }}>{t("customerScan.adding")}</p>
          </div>
        )}

        {/* Чек покупки — под результатом */}
        {result?.success && result.receipt && (
          <div className="fade-up" style={{ marginBottom: 20 }}>
            <ReceiptCard receipt={result.receipt} />
          </div>
        )}

        <div id="qr-reader" style={{ width: '100%', borderRadius: 'var(--radius)', overflow: 'hidden', display: scanning ? 'block' : 'none' }} />

        {scanning && (
          <button className="btn btn-ghost" style={{ marginTop: 16 }} onClick={stopScan}>{t("common.cancel")}</button>
        )}

        {!scanning && !result && !loading && (
          <div className="card" style={{ marginTop: 20, background: 'rgba(58,155,220,0.06)', borderColor: 'rgba(58,155,220,0.2)' }}>
            <p style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6 }}>
              💡 <strong style={{ color: 'var(--text)' }}>{t('customerScan.howItWorks')}:</strong><br />
              {t('customerScan.step1')}<br />
              {t('customerScan.step2')}<br />
              {t('customerScan.step3')}<br />
              {t('customerScan.step4')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
